import React from 'react';
import ReactPaginate from 'react-paginate';
import clsx from 'clsx';

import Icon from '../Icon';
import { Dropdown } from '../Dropdown';
import type { Pagination } from '@hooks/usePaginated';

type PaginatedItemsProps = Pagination & {
  className?: string;
  disabled?: boolean;

  setPage?: (page: number) => void;
  setPageSize?: (size: number) => void;
};

const sizeOptions: OptionType[] = [25, 50, 100, 250, 500, 1_000].map(value => ({
  value: String(value),
  label: String(value),
}));

type PageParams = { selected: number };

const Paginate: React.FC<PaginatedItemsProps> = ({
  className,

  page = 1,
  perPage,
  totalPages,

  setPage,
  setPageSize,
  disabled = false,
}) => {
  const handlePageClick = (e: PageParams) => {
    if (disabled) return;
    setPage?.(e.selected + 1);
  };
  const handlePageSize = (size: string) => {
    if (disabled) return;
    setPageSize?.(parseInt(size));
  };

  return (
    <>
      <ReactPaginate
        className={clsx(
          className,
          'flex gap-x-3 items-center text-medium',
          disabled && 'pointer-events-none opacity-50'
        )}
        disabledClassName="opacity-20 cursor-not-allowed"
        pageClassName="rounded-sm hover:bg-dark cursor-pointer"
        activeClassName="border-2 border-main hover:bg-contrast"
        pageLinkClassName="flex items-center justify-center p-2 size-11"
        nextClassName="hover:bg-dark p-2 rounded-sm"
        previousClassName="hover:bg-dark p-2 rounded-sm"
        breakLabel="..."
        nextLabel={<Icon icon="right" />}
        pageCount={totalPages || 1}
        pageRangeDisplayed={3}
        previousLabel={<Icon icon="left" />}
        renderOnZeroPageCount={null}
        onPageChange={handlePageClick}
        forcePage={page - 1}
      />
      {!!setPageSize && (
        <Dropdown
          options={sizeOptions}
          value={String(perPage)}
          onChange={handlePageSize}
          disabled={disabled}
          grow={false}
          className="ml-md grow-0 max-w-[8rem]"
        />
      )}
    </>
  );
};

export const PaginatePanel: React.FC<PaginatedItemsProps> = props => {
  return (
    <div
      className="flex justify-end"
      aria-disabled={props.disabled || undefined}
    >
      <Paginate {...props} />
    </div>
  );
};

export default React.memo(Paginate) as typeof Paginate;
